import { PageViewEvent, StandardEvents } from '@shopify/events';
import {
  buildSectionSelector,
  morphSection,
  normalizeSectionId,
  sectionRenderer,
} from '@theme/section-renderer';

/**
 * Progressive AJAX navigation for Librairie Resonance's collection rail.
 *
 * Real anchors remain the source of truth. This controller only intercepts a
 * normal same-origin click, renders the complete collection section, and falls
 * back to the untouched URL whenever the soft-navigation contract is not met.
 */

const LINK_SELECTOR = '[data-lr-category-link]';
const STATUS_SELECTOR = '[data-lr-category-status]';
const PENDING_ATTRIBUTE = 'data-lr-pending';
const HISTORY_STATE_KEY = 'lrCategoryNavigation';
const INSTALLATION_KEY = Symbol.for('lr.category-navigation.installed');

const DYNAMIC_HEAD_SELECTORS = [
  'title',
  'link[rel=canonical]',
  'meta[name=description]',
  'meta[property^="og:"]',
  'meta[name^="twitter:"]',
  'script[data-lr-seo="breadcrumb"]',
];

let navigationSequence = 0;
/** @type {AbortController | null} */
let activeRequest = null;
let currentPathAndSearch = getPathAndSearch(window.location);

if (!window[INSTALLATION_KEY]) {
  window[INSTALLATION_KEY] = true;
  markCurrentHistoryEntry();
  document.addEventListener('click', handleClick);
  document.addEventListener(StandardEvents.collectionUpdate, markCurrentHistoryEntry);
  window.addEventListener('popstate', handlePopState);
}

/**
 * Intercepts only an ordinary primary-button click on a rail link.
 * @param {MouseEvent} event
 */
function handleClick(event) {
  if (Shopify.designMode || event.defaultPrevented || event.button !== 0) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  if (!(event.target instanceof Element)) return;

  const anchor = event.target.closest(LINK_SELECTOR);
  if (!(anchor instanceof HTMLAnchorElement)) return;
  if (anchor.hasAttribute('download')) return;
  if (anchor.target && anchor.target !== '_self') return;

  const targetUrl = new URL(anchor.href, window.location.href);
  if (targetUrl.origin !== window.location.origin) return;

  const sectionElement = anchor.closest('.shopify-section');
  if (!(sectionElement instanceof HTMLElement) || !sectionElement.id) return;

  event.preventDefault();

  if (getPathAndSearch(targetUrl) === getPathAndSearch(window.location)) return;

  void navigateToCollection(anchor, sectionElement, targetUrl);
}

/**
 * Fetches, validates, and commits one collection navigation.
 * @param {HTMLAnchorElement} anchor
 * @param {HTMLElement} sectionElement
 * @param {URL} targetUrl
 */
async function navigateToCollection(anchor, sectionElement, targetUrl) {
  const sequence = ++navigationSequence;
  activeRequest?.abort();

  const request = new AbortController();
  activeRequest = request;
  const sectionId = normalizeSectionId(sectionElement.id);

  /*
   * The Livres / Vinyles / Evenements switch is its OWN section, so morphing the collection
   * section never touches it. Reported 2026-08-15: clicking "Vinyles" in the rail from
   * /collections/all left the switch highlighting "Livres" while the URL, the <h1> and the
   * rail all said Vinyles. It reproduces ONLY by clicking — a direct load of the vinyl URL
   * renders the switch correctly, which is why a probe that navigated straight there passed.
   *
   * Requesting it with the CURRENT page's section id is safe across templates, verified
   * against the live store: /collections/all is collection.json and /collections/vinyl-records
   * is collection.boutique.json, so their ids differ — yet the Section Rendering API renders
   * against the REQUESTED URL and echoes back the id we asked for. That matters twice over,
   * because morphSection() looks the id up in the live DOM *and* in the returned fragment;
   * had the response used the target template's id it would not have found the node.
   */
  const switchElement = document.querySelector('[id$="__lr_shop_switch"]');
  const switchId = switchElement ? normalizeSectionId(switchElement.id) : null;

  beginLoading(sectionElement, anchor);

  try {
    // Kick the switch fetch off alongside the collection fetch so it costs no extra
    // round-trip; it is only COMMITTED further down, once the navigation itself is valid.
    const switchHTMLPromise = switchId
      ? sectionRenderer
          .getSectionHTML(switchId, true, new URL(targetUrl), request.signal)
          .catch(() => null)
      : Promise.resolve(null);

    // getSectionHTML mutates the URL it receives by adding section_id, so pass a clone.
    const sectionHTML = await sectionRenderer.getSectionHTML(sectionId, true, new URL(targetUrl), request.signal);
    if (request.signal.aborted || sequence !== navigationSequence) return;

    const metadataTemplate = validateSectionResponse(sectionHTML, sectionId, targetUrl);

    history.pushState({ [HISTORY_STATE_KEY]: true }, '', targetUrl);
    currentPathAndSearch = getPathAndSearch(targetUrl);

    await morphSection(sectionId, sectionHTML);
    if (request.signal.aborted || sequence !== navigationSequence) return;

    syncHead(metadataTemplate.content);
    dispatchPageViewEvent();

    const currentSection = document.getElementById(buildSectionSelector(sectionId));
    if (!(currentSection instanceof HTMLElement)) {
      throw new Error(`Collection section ${sectionId} disappeared after morphing`);
    }

    finishLoading(currentSection);

    // Commit the switch LAST and never let it break a navigation that already succeeded: if
    // this fetch failed, the page content is still correct and only the tab highlight is
    // stale — far better than throwing into the catch below, which hard-navigates the browser.
    const switchHTML = await switchHTMLPromise;
    if (switchId && switchHTML?.trim() && !request.signal.aborted && sequence === navigationSequence) {
      try {
        await morphSection(switchId, switchHTML);
      } catch {
        /* cosmetic only — leave the previous switch state in place */
      }
    }
  } catch (error) {
    if (request.signal.aborted || sequence !== navigationSequence || isAbortError(error)) return;

    clearLoading(sectionElement);
    window.location.assign(targetUrl.toString());
  } finally {
    if (sequence === navigationSequence) activeRequest = null;
  }
}

/**
 * Validates the response before the URL or live DOM changes.
 * @param {string} html
 * @param {string} sectionId
 * @param {URL} targetUrl
 * @returns {HTMLTemplateElement}
 */
function validateSectionResponse(html, sectionId, targetUrl) {
  if (!html.trim()) throw new Error('The collection section response was empty');

  const responseDocument = new DOMParser().parseFromString(html, 'text/html');
  const responseSection = responseDocument.getElementById(buildSectionSelector(sectionId));
  if (!responseSection) throw new Error(`Collection section ${sectionId} was not returned`);

  const activeLink = responseSection.querySelector(`${LINK_SELECTOR}[aria-current=page]`);
  const activeHref = activeLink?.getAttribute('href');
  if (!activeHref || normalizePath(new URL(activeHref, targetUrl.origin).pathname) !== normalizePath(targetUrl.pathname)) {
    throw new Error('The returned collection rail does not match the requested URL');
  }

  const metadataTemplate = responseSection.querySelector('template[data-lr-ajax-head]');
  if (!(metadataTemplate instanceof HTMLTemplateElement)) {
    throw new Error('The returned collection section has no metadata payload');
  }

  const title = metadataTemplate.content.querySelector('title');
  const canonical = metadataTemplate.content.querySelector('link[rel=canonical]');
  const canonicalHref = canonical?.getAttribute('href');
  if (!title?.textContent?.trim() || !canonicalHref) {
    throw new Error('The returned collection metadata is incomplete');
  }

  const canonicalUrl = new URL(canonicalHref, targetUrl.origin);
  if (normalizePath(canonicalUrl.pathname) !== normalizePath(targetUrl.pathname)) {
    throw new Error('The returned collection canonical URL does not match the requested URL');
  }

  return metadataTemplate;
}

/**
 * Replaces the dynamic head nodes with their server-rendered target equivalents.
 * Missing target nodes remove stale values from the previous collection.
 * @param {DocumentFragment} source
 */
function syncHead(source) {
  for (const selector of DYNAMIC_HEAD_SELECTORS) {
    const replacementNodes = Array.from(source.querySelectorAll(selector));
    const currentNodes = Array.from(document.head.querySelectorAll(selector));

    for (const node of currentNodes) node.remove();
    for (const node of replacementNodes) document.head.append(node.cloneNode(true));
  }
}

/** Dispatches the same page-view payload Horizon emits on a hard load. */
function dispatchPageViewEvent() {
  const templateElement = document.querySelector('main[data-template]');
  const template = templateElement?.getAttribute('data-template') || '';

  document.dispatchEvent(
    new PageViewEvent({
      page: {
        template,
        title: document.title,
        url: window.location.href,
      },
    })
  );
}

/**
 * Sets busy state and announces the requested shelf.
 * @param {HTMLElement} sectionElement
 * @param {HTMLAnchorElement} anchor
 */
function beginLoading(sectionElement, anchor) {
  clearLoading(sectionElement);
  sectionElement.setAttribute('aria-busy', 'true');
  anchor.setAttribute(PENDING_ATTRIBUTE, '');
  setStatus(sectionElement, anchor.dataset.lrLoadingMessage || '');
}

/**
 * Clears busy state, announces completion, and restores focus in the new rail.
 * @param {HTMLElement} sectionElement
 */
function finishLoading(sectionElement) {
  clearLoading(sectionElement);
  scrollToSectionTop(sectionElement);

  const activeLink = sectionElement.querySelector(`${LINK_SELECTOR}[aria-current=page]`);
  if (!(activeLink instanceof HTMLAnchorElement)) return;

  setStatus(sectionElement, activeLink.dataset.lrLoadedMessage || '');
  activeLink.focus({ preventScroll: true });
  revealActiveMobileLink(activeLink);
}

/**
 * Scrolls back to the top of the collection grid after a shelf switch, so a new
 * category never lands mid-scroll in what looks like the old one. Only scrolls
 * up, never down, and clears the sticky header height so the heading isn't
 * hidden underneath it.
 *
 * This theme locks html/body with overflow:hidden and does the real
 * scrolling on .page-wrapper (overflow:auto) instead. window.scrollY and
 * window.scrollTo() are disconnected from what's actually visible here — a
 * fix built against them passes every test yet visibly does nothing, so the
 * real scroller must be targeted explicitly.
 *
 * Animated with a short custom rAF tween, not native behavior:'smooth':
 * base.css sets scroll-behavior:smooth globally with a browser-chosen
 * duration that isn't tunable and has already proven fragile on this
 * theme's real scroller, so exact control over "fast" needs its own loop.
 * @param {HTMLElement} sectionElement
 */
function scrollToSectionTop(sectionElement) {
  const scroller = document.querySelector('.page-wrapper') || document.scrollingElement || document.documentElement;
  const isWindowScroller = scroller === document.documentElement || scroller === document.scrollingElement;
  const currentScrollTop = isWindowScroller ? window.scrollY : scroller.scrollTop;

  const headerHeight = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-group-height')) || 0;
  const targetY = Math.max(sectionElement.getBoundingClientRect().top + currentScrollTop - headerHeight, 0);
  if (currentScrollTop <= targetY) return;

  animateScrollTo(scroller, isWindowScroller, currentScrollTop, targetY, 220);
}

/**
 * @param {HTMLElement | Document} scroller
 * @param {boolean} isWindowScroller
 * @param {number} startY
 * @param {number} targetY
 * @param {number} duration
 */
function animateScrollTo(scroller, isWindowScroller, startY, targetY, duration) {
  const distance = targetY - startY;
  let startTime = null;

  const easeOutQuad = (t) => t * (2 - t);

  function step(timestamp) {
    if (startTime === null) startTime = timestamp;
    const progress = Math.min((timestamp - startTime) / duration, 1);
    const y = startY + distance * easeOutQuad(progress);
    if (isWindowScroller) {
      window.scrollTo(0, y);
    } else {
      scroller.scrollTop = y;
    }
    if (progress < 1) requestAnimationFrame(step);
  }
  requestAnimationFrame(step);
}

/** @param {HTMLElement} sectionElement */
function clearLoading(sectionElement) {
  sectionElement.removeAttribute('aria-busy');
  for (const link of sectionElement.querySelectorAll(`[${PENDING_ATTRIBUTE}]`)) {
    link.removeAttribute(PENDING_ATTRIBUTE);
  }
}

/**
 * Writes a polite live-region message.
 * @param {HTMLElement} sectionElement
 * @param {string} message
 */
function setStatus(sectionElement, message) {
  const status = sectionElement.querySelector(STATUS_SELECTOR);
  if (status) status.textContent = message;
}

/**
 * Centers the active mobile pill horizontally without changing vertical scroll.
 * @param {HTMLAnchorElement} activeLink
 */
function revealActiveMobileLink(activeLink) {
  if (!window.matchMedia('(max-width: 989px)').matches) return;

  const list = activeLink.closest('.lr-cat-rail__list');
  if (!(list instanceof HTMLElement) || list.scrollWidth <= list.clientWidth) return;

  const listRect = list.getBoundingClientRect();
  const linkRect = activeLink.getBoundingClientRect();
  if (linkRect.left >= listRect.left && linkRect.right <= listRect.right) return;

  const centeredDelta = linkRect.left - listRect.left - (listRect.width - linkRect.width) / 2;
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  list.scrollTo({ left: list.scrollLeft + centeredDelta, behavior: reduceMotion ? 'auto' : 'smooth' });
}

/** Marks collection entries so category Back/Forward can choose a safe reload. */
function markCurrentHistoryEntry() {
  const currentState = typeof history.state === 'object' && history.state !== null ? history.state : {};
  history.replaceState({ ...currentState, [HISTORY_STATE_KEY]: true }, '', window.location.href);
  currentPathAndSearch = getPathAndSearch(window.location);
}

/**
 * Reloads only when a marked same-document entry changes collection/query state.
 * Hash-only navigation keeps the current document.
 * @param {PopStateEvent} event
 */
function handlePopState(event) {
  const nextPathAndSearch = getPathAndSearch(window.location);
  if (nextPathAndSearch === currentPathAndSearch) return;

  if (event.state?.[HISTORY_STATE_KEY]) {
    window.location.reload();
    return;
  }

  currentPathAndSearch = nextPathAndSearch;
}

/** @param {Location | URL} url */
function getPathAndSearch(url) {
  return `${url.pathname}${url.search}`;
}

/** @param {string} path */
function normalizePath(path) {
  return path.length > 1 ? path.replace(/\/+$/, '') : path;
}

/** @param {unknown} error */
function isAbortError(error) {
  return error instanceof DOMException && error.name === 'AbortError';
}
